/**
 * FCR 19.1 Suspected Misuse — copy for the monthly per-SKU purchase limit UI.
 *
 * The limit itself is configured on the backend (environment variable, default 50);
 * this file only centralises the user-facing wording so it can be reused across the
 * product card, PDP, Mini Cart, Cart tiles and the Cart top-warning banners.
 */
import { MONTHLY_ORDER_LIMIT } from '@models';

/** Monthly purchase limit per SKU used in copy. Single source: `MONTHLY_ORDER_LIMIT` in @models. */
export const ORDER_LIMIT_PER_SKU = MONTHLY_ORDER_LIMIT;

/**
 * AC06/AC07 — the purchase-history sentence only makes sense when something was actually bought
 * earlier this month; a plain cart overflow reports `alreadyPurchased: 0` and must stay silent.
 */
const previouslyOrderedSentence = (quantity: number): string =>
  quantity > 0 ? ` You previously ordered ${quantity} of this item this month.` : '';

export const ORDER_LIMIT_MESSAGES = {
  /** AC01 — disabled "Add to Cart" on product card / PDP (red, error style). */
  limitReached: `Order limit reached. You can order up to ${ORDER_LIMIT_PER_SKU} of this item per month.`,

  /** AC02 / AC05 — reached the limit while in Mini Cart / Cart tile. */
  quantityLimitReached: `You can order up to ${ORDER_LIMIT_PER_SKU} of this item per month.`,

  /**
   * AC06 / AC07 — top app snackbar shown at checkout when the cart is auto-adjusted.
   * Brief summary; the per-product detail lives in the persistent under-"Your items" banner.
   *
   * RID-1365 / RID-1374 — one wording covers every scenario: a reduce, a removal, and both met in
   * the same cart alike speak of the cart quantity being adjusted. The design knows no second
   * summary, and which product was reduced and which was removed is told by the per-product banners.
   */
  snackbarTitle: 'Cart updated',
  snackbarQuantityAdjusted: `Your cart quantity has been adjusted to meet the monthly order limit (${ORDER_LIMIT_PER_SKU}).`,

  /**
   * AC07 — Cart top banner: quantity auto-adjusted to the remaining monthly limit.
   * `previouslyOrdered` reflects the quantity already purchased in prior order(s) this month.
   */
  bannerQuantityAdjusted: (productName: string, previouslyOrdered: number): string =>
    `Your cart quantity of ${productName} has been adjusted to meet the monthly order limit (${ORDER_LIMIT_PER_SKU}).` +
    previouslyOrderedSentence(previouslyOrdered),

  /**
   * AC06 — Cart top banner: product auto-removed to stay within the monthly limit.
   * `previouslyOrdered` reflects the quantity already purchased in prior order(s) this month.
   */
  bannerProductRemoved: (productName: string, previouslyOrdered: number): string =>
    `${productName} has been removed from your cart due to the monthly order limit (${ORDER_LIMIT_PER_SKU}).` +
    previouslyOrderedSentence(previouslyOrdered),

  /**
   * AC07b (Apple) — one consolidated message above the Apple group after excess tiles were removed.
   * `removed` = how many tiles were removed, `productName` = the SKU, `previouslyOrdered` = purchased this month.
   */
  appleRemoved: (removed: number, productName: string, previouslyOrdered: number): string =>
    `${removed} ${removed === 1 ? 'item' : 'items'} of ${productName} ${removed === 1 ? 'has' : 'have'} been ` +
    `removed from your cart to meet the monthly order limit (${ORDER_LIMIT_PER_SKU}).` +
    previouslyOrderedSentence(previouslyOrdered),
} as const;

/**
 * AC02 / AC05 — the message shown under a cart tile; `undefined` when there is none.
 * Keeps the copy in step with the "+" cap: it only speaks once the remaining allowance is reached.
 *
 * RID-1341 — the purchase history is told by the top banner alone; the tile never repeats it.
 */
export const orderLimitTileMessage = (limitReached: boolean): string | undefined =>
  limitReached ? ORDER_LIMIT_MESSAGES.quantityLimitReached : undefined;
